/** @jsxImportSource @emotion/react */
import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "../../appRoutingHelpers";
import { DropdownItem } from "reactstrap";
import clsx from "clsx";
import * as authSelectors from "../auth/authSelectors";
import * as authActions from "../auth/authActions";
import { useTranslation } from "react-i18next";
import { TestIdsEnum } from "../../TestIdsEnum";
import { NavigationLink } from "../../components/NavigationLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { DropdownNavigation } from "../../components/DropdownNavigation";
import { ExternalNavLink } from "../../components/ExternalNavLink";
import { OrganizationTopbarSection } from "./OrganizationTopbarSection";
import { localStorageScrollPositionName } from "../products/ProductsList";
import { CountryDropdown } from "./CountryDropdown";
import { css } from "@emotion/react";
import { breakpoints } from "../../components/breakpoints";
import { orgsWithAutoCampaigns, useIsOrganization } from "../../util";
import { config } from "../../config";

type MenuItems =
  | "home"
  | "orders"
  | "users"
  | "restaurants"
  | "products"
  | "loyalty"
  | "communication"
  | "settings"
  | "menus"
  | "analytics";

interface Props {}

const countryDropdownStyle = css`
  display: none;
  ${breakpoints.mobile} {
    display: flex;
    margin-top: 8px;
  }
`;

const TopBar: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation(undefined);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // <state>
  const [menuDropdownOpen, setMenuDropdownOpen] = React.useState<boolean>(
    false
  );
  const [activeMenu, setActiveMenu] = React.useState<MenuItems>("home");

  const toggleMenuDropdown = () => {
    setMenuDropdownOpen(!menuDropdownOpen);
  };

  const onOrganizationSelect = (organizationKeyName: string) => {
    selectOrganization(organizationKeyName);
    navigate(routes.home(organizationKeyName));
  };
  // </state>

  // <dispatch>
  const doLogout = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    dispatch(authActions.logoutAction());
  };
  const selectOrganization = (organizationKeyName: string) =>
    dispatch(authActions.selectOrganizationAction(organizationKeyName));
  // </dispatch>

  // <selectors>
  const hasAccessToBeacons = useSelector(authSelectors.hasAccessToBeacons);
  const hasAccessToPromoCodes = useSelector(
    authSelectors.hasAccessToPromoCodes
  );
  const hasAccessToOrgUISettings = useSelector(
    authSelectors.hasAccessToUiSettings
  );
  const hasAccessToOrganizations = useSelector(
    authSelectors.hasAccessToOrganizations
  );
  const hasAccessToUsers = useSelector(authSelectors.hasAccessToUsers);
  const hasAccessToVenues = useSelector(authSelectors.hasAccessToVenues);
  const hasAccessToEmailTemplates = useSelector(
    authSelectors.hasAccessToEmailTemplates
  );
  const hasAccessToAdmins = useSelector(authSelectors.hasAccessToAdmins);
  const hasAccessToMemberCardTypes = useSelector(
    authSelectors.hasAccessToMemberCardTypes
  );
  const hasAccessToPartnerships = useSelector(
    authSelectors.hasAccessToPartnerships
  );
  const selectedOrganizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const hasAccessToShops = useSelector(authSelectors.hasAccessToShops);
  const hasAccessToChallenges = useSelector(
    authSelectors.hasAccessToChallenges
  );
  const hasAccessToVoucherStacks = useSelector(
    authSelectors.hasAccessToVoucherStacks
  );
  const hasAccessToDeliveryRules = useSelector(
    authSelectors.hasAccessToDeliveryRules
  );
  const hasAccessToIntegrations = useSelector(
    authSelectors.hasAccessToIntegrations
  );
  const hasAccessToAnalytics = useSelector(authSelectors.hasAccessToAnalytics);
  const isSuperAdmin = useSelector(authSelectors.isSuperAdmin);

  // TODO: Remove when frontend and backend is ready, and shop menus if user has access to shop
  const hasAccessToMenus = useSelector(authSelectors.hasAccessToMenus);

  const usersRoutes = [routes.userList(selectedOrganizationKeyName)];
  const menuRoutes = [routes.menuList(selectedOrganizationKeyName)];
  const restaurantRoutes = [routes.venueList(selectedOrganizationKeyName)];
  const productsRoutes = [
    routes.shopProductCategoryList(selectedOrganizationKeyName),
    routes.shopProductAdditionsList(selectedOrganizationKeyName),
    routes.products(selectedOrganizationKeyName),
    routes.kitchenCategories(selectedOrganizationKeyName),
    routes.packingItems(selectedOrganizationKeyName),
  ];
  const loyaltyRoutes = [
    routes.promotionCodeList(selectedOrganizationKeyName),
    routes.partnershipList(selectedOrganizationKeyName),
    routes.memberCardTypes(selectedOrganizationKeyName),
    routes.voucherTypeList(selectedOrganizationKeyName),
    routes.punchCards(selectedOrganizationKeyName),
    routes.challenges(selectedOrganizationKeyName),
    routes.appHome(selectedOrganizationKeyName),
    routes.news(selectedOrganizationKeyName),
    routes.autoCampaigns(selectedOrganizationKeyName),
    routes.campaigns(selectedOrganizationKeyName),
    routes.campaignsNew(selectedOrganizationKeyName),
    routes.productGroups(selectedOrganizationKeyName),
    routes.productGroupNew(selectedOrganizationKeyName),
  ];
  const communicationRoutes = [
    routes.emailTemplates(selectedOrganizationKeyName),
  ];
  const settingsRoutes = [
    routes.shopList(selectedOrganizationKeyName),
    routes.adminUserList(selectedOrganizationKeyName),
    routes.beaconStatus(selectedOrganizationKeyName),
    routes.organizationUISettingsEdit(selectedOrganizationKeyName),
    routes.organizationEventLog(selectedOrganizationKeyName),
    routes.userTermList(selectedOrganizationKeyName),
    routes.deliveryRules(selectedOrganizationKeyName),
    routes.integrationsList(selectedOrganizationKeyName),
    routes.productTags(selectedOrganizationKeyName),
  ];
  const homeRoutes = [routes.home(selectedOrganizationKeyName)];
  const analyticsRoutes = [routes.reports(selectedOrganizationKeyName)];
  const ordersRoutes = [routes.orderList(selectedOrganizationKeyName)];
  const organizationsRoutes = [
    routes.organizationsList(selectedOrganizationKeyName),
  ];

  const showAutoCampaigns_ = useIsOrganization(orgsWithAutoCampaigns);

  const isInPathname = (route: string) => {
    const regex = new RegExp(`${route}/*`);
    return regex.test(location.pathname);
  };

  // important: longer routes need to go first since there are routes starting with same substring
  React.useEffect(() => {
    if (usersRoutes.some(isInPathname)) {
      setActiveMenu("users");
    } else if (productsRoutes.some(isInPathname)) {
      setActiveMenu("products");
    } else if (restaurantRoutes.some(isInPathname)) {
      setActiveMenu("restaurants");
    } else if (loyaltyRoutes.some(isInPathname)) {
      setActiveMenu("loyalty");
    } else if (communicationRoutes.some(isInPathname)) {
      setActiveMenu("communication");
    } else if (ordersRoutes.some(isInPathname)) {
      setActiveMenu("orders");
    } else if (menuRoutes.some(isInPathname)) {
      setActiveMenu("menus");
    } else if (settingsRoutes.some(isInPathname)) {
      setActiveMenu("settings");
    } else if (analyticsRoutes.some(isInPathname)) {
      setActiveMenu("analytics");
    } else if (homeRoutes.some(isInPathname)) {
      setActiveMenu("home");
    } else if (organizationsRoutes.some(isInPathname)) {
      setActiveMenu("settings");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, selectedOrganizationKeyName]);

  const showAutoCampaigns =
    config.environment === "production" ? showAutoCampaigns_ : true;
  const dropdownItemStyle = "p-1 bg-transparent";
  const hasAccessToSettings =
    hasAccessToBeacons ||
    hasAccessToOrgUISettings ||
    hasAccessToUsers ||
    hasAccessToShops ||
    hasAccessToAdmins ||
    hasAccessToDeliveryRules ||
    hasAccessToOrganizations;

  return (
    <>
      <OrganizationTopbarSection
        toggleMenuDropdown={toggleMenuDropdown}
        onOrganizationSelect={onOrganizationSelect}
        logOut={doLogout}
      />
      <div
        className={clsx("header", "d-lg-flex", "p-0", "collapse", {
          show: menuDropdownOpen,
        })}
        id="headerMenuCollapse"
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg order-lg-first">
              <div css={countryDropdownStyle}>
                <CountryDropdown showVerticalDivider={false} />
              </div>
              <ul
                className="nav nav-tabs border-0 flex-column flex-lg-row py-2"
                style={{ gap: "8px" }}
              >
                <NavigationLink
                  data-test={TestIdsEnum.TopBarUsersListLink}
                  exact={true}
                  to={routes.home(selectedOrganizationKeyName)}
                  isTopBarLink={true}
                  onClick={toggleMenuDropdown}
                >
                  {`${t("top_bar.home")}`}
                </NavigationLink>
                <NavigationLink
                  data-test={TestIdsEnum.TopBarUsersListLink}
                  to={routes.orderList(selectedOrganizationKeyName)}
                  isTopBarLink={true}
                  locked={!hasAccessToShops}
                  onClick={toggleMenuDropdown}
                >
                  Orders
                </NavigationLink>
                <NavigationLink
                  data-test={TestIdsEnum.TopBarUsersListLink}
                  to={routes.userList(selectedOrganizationKeyName)}
                  isTopBarLink={true}
                  locked={!hasAccessToUsers}
                  onClick={toggleMenuDropdown}
                >
                  {`${t("top_bar.users")}`}
                </NavigationLink>
                <DropdownNavigation
                  isActive={activeMenu === "products"}
                  toggleTitle="Products"
                >
                  <DropdownItem
                    className={dropdownItemStyle}
                    onClick={() => {
                      if (localStorage) {
                        localStorage.removeItem(localStorageScrollPositionName);
                      }
                    }}
                  >
                    <NavigationLink
                      data-test={TestIdsEnum.TopBarVenuesListLink}
                      to={routes.products(selectedOrganizationKeyName)}
                      icon={<FontAwesomeIcon icon={solid("cart-shopping")} />}
                      locked={!hasAccessToShops}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      Products & additions
                    </NavigationLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      data-test={TestIdsEnum.TopBarVenuesListLink}
                      to={routes.shopProductAdditionsList(
                        selectedOrganizationKeyName
                      )}
                      icon={<FontAwesomeIcon icon={solid("circle-plus")} />}
                      locked={!hasAccessToShops}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      Addition groups
                    </NavigationLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      data-test={TestIdsEnum.TopBarVenuesListLink}
                      to={routes.shopProductCategoryList(
                        selectedOrganizationKeyName
                      )}
                      icon={<FontAwesomeIcon icon={solid("tags")} />}
                      locked={!hasAccessToShops}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      Product categories
                    </NavigationLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      to={routes.kitchenCategories(selectedOrganizationKeyName)}
                      icon={<FontAwesomeIcon icon={solid("list")} />}
                      locked={!hasAccessToShops}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      Kitchen categories
                    </NavigationLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      to={routes.packingItems(selectedOrganizationKeyName)}
                      icon={<FontAwesomeIcon icon={solid("boxes-packing")} />}
                      locked={!hasAccessToShops}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      Packing items
                    </NavigationLink>
                  </DropdownItem>
                </DropdownNavigation>

                {/* TODO: Check on hasAccessToShop instead */}
                {hasAccessToMenus && (
                  <NavigationLink
                    to={routes.menuList(selectedOrganizationKeyName)}
                    isTopBarLink={true}
                    onClick={toggleMenuDropdown}
                  >
                    Menus
                  </NavigationLink>
                )}
                <NavigationLink
                  data-test={TestIdsEnum.TopBarVenuesListLink}
                  to={routes.venueList(selectedOrganizationKeyName)}
                  isTopBarLink={true}
                  locked={!hasAccessToVenues}
                  onClick={toggleMenuDropdown}
                >
                  {`${t("top_bar.restaurants")}`}
                </NavigationLink>
                <DropdownNavigation
                  isActive={activeMenu === "loyalty"}
                  toggleTitle="Loyalty"
                >
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      to={routes.appHome(selectedOrganizationKeyName)}
                      icon={<FontAwesomeIcon icon={solid("mobile-iphone")} />}
                      locked={!hasAccessToShops}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      App home
                    </NavigationLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      to={routes.news(selectedOrganizationKeyName)}
                      icon={<FontAwesomeIcon icon={solid("newspaper")} />}
                      locked={!hasAccessToShops}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      News
                    </NavigationLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      to={routes.voucherTypeList(selectedOrganizationKeyName)}
                      icon={<FontAwesomeIcon icon={solid("ticket")} />}
                      locked={!hasAccessToShops}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      Voucher templates
                    </NavigationLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      to={routes.memberCardTypes(selectedOrganizationKeyName)}
                      icon={<FontAwesomeIcon icon={solid("id-card")} />}
                      locked={!hasAccessToMemberCardTypes}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      Membercard templates
                    </NavigationLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      to={routes.partnershipList(selectedOrganizationKeyName)}
                      icon={<FontAwesomeIcon icon={solid("handshake")} />}
                      locked={!hasAccessToPartnerships}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      Partnerships
                    </NavigationLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      to={routes.promotionCodeList(selectedOrganizationKeyName)}
                      icon={<FontAwesomeIcon icon={solid("badge-percent")} />}
                      locked={!hasAccessToPromoCodes}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      Promotion codes
                    </NavigationLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      to={routes.challenges(selectedOrganizationKeyName)}
                      icon={<FontAwesomeIcon icon={solid("trophy-star")} />}
                      locked={!hasAccessToChallenges}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      Challenges
                    </NavigationLink>
                  </DropdownItem>
                  {showAutoCampaigns && (
                    <DropdownItem className={dropdownItemStyle}>
                      <NavigationLink
                        to={routes.autoCampaigns(selectedOrganizationKeyName)}
                        onClick={toggleMenuDropdown}
                        icon={<FontAwesomeIcon icon={solid("percent")} />}
                      >
                        Auto campaigns
                      </NavigationLink>
                    </DropdownItem>
                  )}
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      to={routes.punchCards(selectedOrganizationKeyName)}
                      icon={<FontAwesomeIcon icon={solid("layer-group")} />}
                      locked={!hasAccessToVoucherStacks}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      Prepaid (Voucher Stacks)
                    </NavigationLink>
                  </DropdownItem>
                </DropdownNavigation>
                <DropdownNavigation
                  isActive={activeMenu === "communication"}
                  toggleTitle="Communication"
                >
                  <DropdownItem className={dropdownItemStyle}>
                    <ExternalNavLink
                      href={"https://dashboard.onesignal.com/login"}
                      icon={<FontAwesomeIcon icon={solid("envelopes-bulk")} />}
                      isTopBarLink={false}
                    >
                      Email
                    </ExternalNavLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <ExternalNavLink
                      href={"https://dashboard.onesignal.com/login"}
                      icon={<FontAwesomeIcon icon={solid("mobile-iphone")} />}
                      isTopBarLink={false}
                    >
                      In-app push
                    </ExternalNavLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <ExternalNavLink
                      href={"https://dashboard.onesignal.com/login"}
                      icon={<FontAwesomeIcon icon={solid("paper-plane")} />}
                      isTopBarLink={false}
                    >
                      Push
                    </ExternalNavLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <ExternalNavLink
                      href={"https://dashboard.onesignal.com/login"}
                      icon={<FontAwesomeIcon icon={solid("brain-circuit")} />}
                      isTopBarLink={false}
                    >
                      Intelligent journeys
                    </ExternalNavLink>
                  </DropdownItem>
                  <DropdownItem className={dropdownItemStyle}>
                    <NavigationLink
                      to={routes.emailTemplates(selectedOrganizationKeyName)}
                      icon={<FontAwesomeIcon icon={solid("envelope")} />}
                      locked={!hasAccessToEmailTemplates}
                      width="100%"
                      onClick={toggleMenuDropdown}
                    >
                      Email templates
                    </NavigationLink>
                  </DropdownItem>
                </DropdownNavigation>
                {(hasAccessToAnalytics || isSuperAdmin) && (
                  <DropdownNavigation
                    isActive={activeMenu === "analytics"}
                    toggleTitle="Analytics"
                  >
                    <DropdownItem className={dropdownItemStyle}>
                      <ExternalNavLink
                        href={`${config.heapsGoAdminUrl}/hgo/${selectedOrganizationKeyName}/analytics`}
                        // Our own admin: keep the referrer, so the new admin
                        // can tell visits that came from here.
                        rel="noopener"
                        icon={<FontAwesomeIcon icon={solid("chart-line")} />}
                      >
                        Analytics
                      </ExternalNavLink>
                    </DropdownItem>
                    <DropdownItem className={dropdownItemStyle}>
                      <ExternalNavLink
                        href={"https://mixpanel.com/login/"}
                        icon={<FontAwesomeIcon icon={solid("chart-mixed")} />}
                      >
                        KPI Dashboard
                      </ExternalNavLink>
                    </DropdownItem>
                    <DropdownItem className={dropdownItemStyle}>
                      <ExternalNavLink
                        href={"https://mixpanel.com/login/"}
                        icon={<FontAwesomeIcon icon={solid("dollar-sign")} />}
                      >
                        Financial reporting
                      </ExternalNavLink>
                    </DropdownItem>
                    <DropdownItem className={dropdownItemStyle}>
                      {/* Lands on the "Reports have moved" signpost, which links on to
          the new admin. */}
                      <NavigationLink
                        to={routes.reports(selectedOrganizationKeyName)}
                        icon={<FontAwesomeIcon icon={solid("book-open")} />}
                        onClick={toggleMenuDropdown}
                      >
                        Reports
                      </NavigationLink>
                    </DropdownItem>
                  </DropdownNavigation>
                )}
                {hasAccessToSettings && (
                  <DropdownNavigation
                    isActive={activeMenu === "settings"}
                    toggleTitle="Settings"
                  >
                    {hasAccessToAdmins && (
                      <DropdownItem className={dropdownItemStyle}>
                        <NavigationLink
                          data-test={TestIdsEnum.TopBarUsersListLink}
                          to={routes.adminUserList(selectedOrganizationKeyName)}
                          icon={<FontAwesomeIcon icon={solid("house")} />}
                          onClick={toggleMenuDropdown}
                        >
                          User admins
                        </NavigationLink>
                      </DropdownItem>
                    )}
                    {hasAccessToShops && (
                      <DropdownItem className={dropdownItemStyle}>
                        <NavigationLink
                          to={routes.shopList(selectedOrganizationKeyName)}
                          exact={true}
                          icon={<FontAwesomeIcon icon={solid("shop")} />}
                          onClick={toggleMenuDropdown}
                        >
                          Shops
                        </NavigationLink>
                      </DropdownItem>
                    )}
                    {hasAccessToDeliveryRules && (
                      <DropdownItem className={dropdownItemStyle}>
                        <NavigationLink
                          to={routes.deliveryRules(selectedOrganizationKeyName)}
                          icon={<FontAwesomeIcon icon={solid("section")} />}
                          onClick={toggleMenuDropdown}
                        >
                          Delivery rules
                        </NavigationLink>
                      </DropdownItem>
                    )}
                    {hasAccessToIntegrations && (
                      <DropdownItem className={dropdownItemStyle}>
                        <NavigationLink
                          to={routes.integrationsList(
                            selectedOrganizationKeyName
                          )}
                          onClick={toggleMenuDropdown}
                          icon={
                            <FontAwesomeIcon icon={solid("network-wired")} />
                          }
                        >
                          Integrations
                        </NavigationLink>
                      </DropdownItem>
                    )}
                    {hasAccessToBeacons && (
                      <DropdownItem className={dropdownItemStyle}>
                        <NavigationLink
                          to={routes.beaconStatus(selectedOrganizationKeyName)}
                          icon={<FontAwesomeIcon icon={solid("microchip")} />}
                          onClick={toggleMenuDropdown}
                        >
                          Beacons
                        </NavigationLink>
                      </DropdownItem>
                    )}
                    {hasAccessToOrgUISettings && (
                      <DropdownItem className={dropdownItemStyle}>
                        <NavigationLink
                          to={routes.organizationUISettingsEdit(
                            selectedOrganizationKeyName
                          )}
                          onClick={toggleMenuDropdown}
                          icon={<FontAwesomeIcon icon={solid("sidebar")} />}
                        >
                          Organization UI settings
                        </NavigationLink>
                      </DropdownItem>
                    )}
                    {hasAccessToUsers && (
                      <DropdownItem className={dropdownItemStyle}>
                        <NavigationLink
                          to={routes.organizationEventLog(
                            selectedOrganizationKeyName
                          )}
                          onClick={toggleMenuDropdown}
                          icon={
                            <FontAwesomeIcon icon={solid("chart-waterfall")} />
                          }
                        >
                          Event logs
                        </NavigationLink>
                      </DropdownItem>
                    )}
                    <DropdownItem className={dropdownItemStyle}>
                      <NavigationLink
                        to={routes.productTags(selectedOrganizationKeyName)}
                        onClick={toggleMenuDropdown}
                        exact={true}
                        icon={<FontAwesomeIcon icon={solid("tag")} />}
                      >
                        Product tags
                      </NavigationLink>
                    </DropdownItem>
                    {hasAccessToOrganizations && (
                      <DropdownItem className={dropdownItemStyle}>
                        <NavigationLink
                          to={routes.organizationsList(
                            selectedOrganizationKeyName
                          )}
                          onClick={toggleMenuDropdown}
                          exact={true}
                          icon={<FontAwesomeIcon icon={solid("buildings")} />}
                        >
                          Organizations
                        </NavigationLink>
                      </DropdownItem>
                    )}
                    <DropdownItem className={dropdownItemStyle}>
                      <NavigationLink
                        to={routes.userTermList(selectedOrganizationKeyName)}
                        exact={true}
                        icon={<FontAwesomeIcon icon={solid("file-contract")} />}
                        onClick={toggleMenuDropdown}
                      >
                        User terms
                      </NavigationLink>
                    </DropdownItem>
                  </DropdownNavigation>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TopBar;
